import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link } from "react-router-dom";
import apiClient from "../../services/apiClient";
import SEO from "../../components/SEO";
import { getPriorityLabel, getQualificationStatusLabel } from "../constants/prospectLabels";
import DataGridFr from "../components/DataGridFr";

const STATUS_FILTERS = [
  { key: "all", label: "Pipeline" },
  { key: "pending_review", label: "Brouillons", statuses: ["pending_review"] },
  { key: "new", label: "Trouvés", statuses: ["entity_found", "raw_lead", "qualifiable"] },
  { key: "to_enrich", label: "À enrichir", statuses: ["to_enrich"] },
  { key: "qualified", label: "Qualifiés", statuses: ["qualified"] },
  { key: "priority", label: "Prioritaires", statuses: ["priority"] },
  { key: "contacted", label: "Contactés", statuses: ["contacted"] },
  { key: "excluded", label: "Exclus", statuses: ["excluded"] },
];

const STATUS_OPTIONS = [
  { value: "pending_review", label: "Brouillon", color: "default" },
  { value: "raw_lead", label: "Trouvé", color: "default" },
  { value: "to_enrich", label: "À enrichir", color: "info" },
  { value: "qualified", label: "Qualifié", color: "primary" },
  { value: "priority", label: "Prioritaire", color: "warning" },
  { value: "contacted", label: "Contacté", color: "success" },
  { value: "excluded", label: "Exclu", color: "error" },
];

function scoreColor(score) {
  if (score >= 60) return "success";
  if (score >= 35) return "warning";
  return "default";
}

export default function AdminProspectsPipelinePage() {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [bulkBusy, setBulkBusy] = useState("");
  const [feedback, setFeedback] = useState({ type: "", text: "" });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoadError("");
      const result = await apiClient.adminProspectsList("limit=500&include_pending_review=1");
      setRows(result.rows || []);
    } catch (error) {
      setLoadError(error.message || "Impossible de charger le pipeline.");
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return rows.filter((r) => r.qualification_status !== "pending_review");
    const f = STATUS_FILTERS.find((s) => s.key === statusFilter);
    return f?.statuses ? rows.filter((r) => f.statuses.includes(r.qualification_status)) : rows;
  }, [rows, statusFilter]);

  const statusCounts = useMemo(() => {
    const pipelineCount = rows.filter((r) => r.qualification_status !== "pending_review").length;
    const counts = { all: pipelineCount };
    for (const f of STATUS_FILTERS) {
      if (f.key === "all") continue;
      if (f.statuses) counts[f.key] = rows.filter((r) => f.statuses.includes(r.qualification_status)).length;
    }
    return counts;
  }, [rows]);

  const setStatus = async (id, qualification_status) => {
    setBusyId(id);
    try {
      await apiClient.adminProspectPatch(id, { qualification_status });
      setFeedback({ type: "success", text: "Statut mis à jour." });
      await load();
    } catch (error) { setFeedback({ type: "error", text: error.message }); }
    finally { setBusyId(""); }
  };

  const runScoring = async (id) => {
    setBusyId(id);
    try {
      await apiClient.adminProspectRecalculate(id);
      setFeedback({ type: "success", text: "Score recalculé." });
      await load();
    } catch (error) { setFeedback({ type: "error", text: error.message }); }
    finally { setBusyId(""); }
  };

  const handleDelete = async (id) => {
    setBusyId(id);
    try {
      await apiClient.adminProspectDelete(id);
      setFeedback({ type: "success", text: "Prospect supprimé." });
      setConfirmDelete(null);
      await load();
    } catch (error) { setFeedback({ type: "error", text: error.message }); }
    finally { setBusyId(""); }
  };

  const handleArchive = async (id) => {
    setBusyId(id);
    try {
      await apiClient.adminProspectArchive(id);
      setFeedback({ type: "success", text: "Prospect archivé." });
      setConfirmArchive(null);
      await load();
    } catch (error) { setFeedback({ type: "error", text: error.message }); }
    finally { setBusyId(""); }
  };

  const enrichAll = async (id) => {
    setBusyId(id);
    try {
      await apiClient.adminProspectEnrichAll(id);
      setFeedback({ type: "success", text: "Enrichissement terminé." });
      await load();
    } catch (error) { setFeedback({ type: "error", text: error.message }); }
    finally { setBusyId(""); }
  };

  const convertToLead = async (id) => {
    setBusyId(id);
    try {
      await apiClient.adminProspectConvertToLead(id);
      setFeedback({ type: "success", text: "Converti en lead." });
      await load();
    } catch (error) { setFeedback({ type: "error", text: error.message }); }
    finally { setBusyId(""); }
  };

  const columns = useMemo(() => [
    {
      field: "company_name",
      headerName: "Entreprise",
      flex: 1.5,
      minWidth: 180,
      renderCell: (p) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>{p.value}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{p.row.location_label || p.row.website_domain || ""}</Typography>
        </Box>
      ),
    },
    {
      field: "qualification_status",
      headerName: "Statut",
      width: 150,
      type: "singleSelect",
      valueOptions: STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label })),
      renderCell: (p) => {
        const opt = STATUS_OPTIONS.find((s) => s.value === p.value) || STATUS_OPTIONS[0];
        return (
          <TextField select size="small" value={p.value || "raw_lead"}
            disabled={busyId === p.row.id}
            onChange={(e) => { e.stopPropagation(); setStatus(p.row.id, e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            sx={{ minWidth: 120, "& .MuiSelect-select": { py: 0.25, fontSize: "0.8rem" } }}>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                <Chip size="small" label={s.label} color={s.color} variant={s.value === p.value ? "filled" : "outlined"} sx={{ cursor: "pointer" }} />
              </MenuItem>
            ))}
          </TextField>
        );
      },
    },
    {
      field: "global_score",
      headerName: "Score",
      width: 90,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: (p) => <Chip size="small" label={`${p.value ?? "-"}/100`} color={scoreColor(p.value)} />,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 160,
      valueGetter: (value, row) => row.primary_contact?.email || "",
      renderCell: (p) => (
        <Typography variant="caption" noWrap color={p.value ? "text.primary" : "error.main"}>
          {p.value || "—"}
        </Typography>
      ),
    },
    {
      field: "website_domain",
      headerName: "Site",
      flex: 0.8,
      minWidth: 120,
      renderCell: (p) => (
        <Typography variant="caption" noWrap color={p.row.website_url ? "primary.main" : "error.main"}>
          {p.value || (p.row.website_url ? "Oui" : "—")}
        </Typography>
      ),
    },
    {
      field: "priority_level",
      headerName: "Priorité",
      width: 110,
      type: "singleSelect",
      valueOptions: ["critical", "high", "medium", "low", "none"],
      renderCell: (p) => (
        <Typography variant="caption">{getPriorityLabel(p.value)}</Typography>
      ),
    },
    {
      field: "completeness_pct",
      headerName: "Complet.",
      width: 80,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Typography variant="caption">{p.value ?? "-"}%</Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 220,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderCell: (p) => {
        const id = p.row.id;
        const busy = busyId === id;
        return (
          <Box sx={{ display: "flex", gap: 0.25, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Scorer">
              <Button size="small" sx={{ minWidth: 0, px: 0.5, fontSize: "0.7rem" }} disabled={busy} onClick={() => runScoring(id)}>Score</Button>
            </Tooltip>
            <Tooltip title="Enrichir toutes sources">
              <Button size="small" sx={{ minWidth: 0, px: 0.5, fontSize: "0.7rem" }} disabled={busy} onClick={() => enrichAll(id)}>Enrich</Button>
            </Tooltip>
            {p.row.primary_contact?.email && (
              <Tooltip title="Convertir en lead">
                <Button size="small" sx={{ minWidth: 0, px: 0.5, fontSize: "0.7rem" }} disabled={busy} color="success" onClick={() => convertToLead(id)}>Lead</Button>
              </Tooltip>
            )}
            <Tooltip title="Ouvrir la fiche">
              <IconButton size="small" component={Link} to={`/admin/prospects/${id}`} aria-label="Ouvrir">
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Archiver">
              <IconButton size="small" disabled={busy} onClick={() => setConfirmArchive(p.row)} aria-label="Archiver">
                <ArchiveOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton size="small" color="error" disabled={busy} onClick={() => setConfirmDelete(p.row)} aria-label="Supprimer">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ], [busyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const bulkIds = selected.length > 0 ? selected : filtered.map((r) => r.id);
  const bulkCount = selected.length > 0 ? selected.length : filtered.length;
  const bulkLabel = selected.length > 0 ? `sélectionnés (${selected.length})` : `filtrés (${filtered.length})`;

  return (
    <>
      <SEO title="Pipeline Prospects | DigiBuddy" noIndex noFollow />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton component={Link} to="/admin/prospection" size="small" aria-label="Retour"><ArrowBackIcon /></IconButton>
          <Typography variant="h5">
            Pipeline ({statusCounts.all}
            {statusCounts.pending_review > 0 ? ` · ${statusCounts.pending_review} brouillon(s)` : ""})
          </Typography>
        </Box>
        <IconButton onClick={load} size="small" aria-label="Recharger"><RefreshIcon /></IconButton>
      </Box>

      {/* FILTRES PAR STATUT */}
      <Box sx={{ display: "flex", gap: 0.5, mb: 1.5, flexWrap: "wrap" }}>
        {STATUS_FILTERS.map((f) => (
          <Chip key={f.key} label={`${f.label} (${statusCounts[f.key] || 0})`} clickable
            variant={statusFilter === f.key ? "filled" : "outlined"}
            color={statusFilter === f.key ? "primary" : "default"}
            onClick={() => setStatusFilter(f.key)} size="small" />
        ))}
      </Box>

      {/* ACTIONS GROUPEES */}
      <Paper sx={{ p: 1, mb: 1.5, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>Actions sur {bulkLabel} :</Typography>
        <Button size="small" variant="outlined" disabled={bulkCount === 0 || Boolean(bulkBusy)}
          onClick={() => setConfirmBulk({ action: "recalculate", label: "Scorer", estimate: `~${Math.ceil(bulkCount * 0.5)}s` })}>
          {bulkBusy === "recalculate" ? "..." : `Scorer (${bulkCount})`}
        </Button>
        <Button size="small" variant="outlined" disabled={bulkCount === 0 || Boolean(bulkBusy)}
          onClick={() => setConfirmBulk({ action: "enrich_scan", label: "Scanner sites", estimate: `~${Math.ceil(bulkCount * 6)}s` })}>
          {bulkBusy === "enrich_scan" ? "..." : `Scanner sites (${bulkCount})`}
        </Button>
        <Button size="small" variant="outlined" disabled={bulkCount === 0 || Boolean(bulkBusy)}
          onClick={() => setConfirmBulk({ action: "enrich_all", label: "Enrichir tout", estimate: `~${Math.ceil(bulkCount * 15)}s` })}>
          {bulkBusy === "enrich_all" ? "..." : `Enrichir tout (${bulkCount})`}
        </Button>
      </Paper>

      {loadError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setLoadError("")}>{loadError}</Alert>}
      {feedback.text && <Alert severity={feedback.type || "info"} sx={{ mb: 1.5 }} onClose={() => setFeedback({ type: "", text: "" })}>{feedback.text}</Alert>}

      {/* TABLEAU PRINCIPAL */}
      <Paper sx={{ width: "100%" }}>
        <DataGridFr
          rows={filtered}
          columns={columns}
          checkboxSelection
          rowSelectionModel={selected}
          onRowSelectionModelChange={setSelected}
          initialSortModel={[{ field: "global_score", sort: "desc" }]}
          pageSize={25}
        />
      </Paper>

      {/* DIALOGS */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Supprimer ce prospect ?</DialogTitle>
        <DialogContent>
          <Typography>{confirmDelete?.company_name} sera supprimé définitivement.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={() => confirmDelete && handleDelete(confirmDelete.id)}>Supprimer</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmArchive)} onClose={() => setConfirmArchive(null)}>
        <DialogTitle>Archiver ce prospect ?</DialogTitle>
        <DialogContent>
          <Typography>{confirmArchive?.company_name} sera retiré du pipeline actif.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmArchive(null)}>Annuler</Button>
          <Button variant="contained" onClick={() => { if (confirmArchive) { handleArchive(confirmArchive.id); } }}>Archiver</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmBulk)} onClose={() => setConfirmBulk(null)}>
        <DialogTitle>Confirmer l'action groupée</DialogTitle>
        <DialogContent>
          <Typography><b>{confirmBulk?.label}</b> sur <b>{bulkCount}</b> prospect(s).</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Durée estimée : {confirmBulk?.estimate}.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulk(null)}>Annuler</Button>
          <Button variant="contained" onClick={async () => {
            const { action } = confirmBulk;
            setConfirmBulk(null);
            setBulkBusy(action);
            setFeedback({ type: "", text: "" });
            try {
              const ids = bulkIds;
              let payload = { ids, action };
              if (action === "enrich_scan") payload = { ids, action: "enrich", options: { source: "website_scan" } };
              const report = await apiClient.adminProspectsBulkAction(payload);
              setFeedback({ type: "success", text: `${report.success}/${report.total} traité(s).` });
              await load();
            } catch (error) { setFeedback({ type: "error", text: error.message }); }
            finally { setBulkBusy(""); }
          }}>Lancer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
